/**
 * useList hook - Access a single list (and its items) reactively
 * UI components should ONLY use hooks + domain models.
 */

import { useEffect, useMemo, useState } from 'react'
import { useServices } from '../../core/services/ServicesContext'
import { eventProcessor } from '../../core/events/processor'
import type { Item, List, Scenario } from '../../core/domain/models'
import { selectSortedListItems } from '../../core/state/list-selectors'

export interface UseListResult {
  list: List | null
  items: Item[]
  loading: boolean
  error: string | null
  accessDenied: boolean
  dismissAccessDenied: () => void
}

type ScenarioWithData = Scenario & { lists: List[] }

export function useList(scenarioId: string, listId: string): UseListResult {
  const { memoryState } = useServices()
  
  // Initialize state
  const [scenarioData, setScenarioData] = useState<ScenarioWithData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)

  useEffect(() => {
    // Eagerly load from memory if available
    if (memoryState.isScenarioLoaded(scenarioId)) {
      setScenarioData(memoryState.getScenarioWithData(scenarioId) ?? null)
      setLoading(false)
    }
  }, [memoryState, scenarioId])

  useEffect(() => {
    let mounted = true

    const load = async () => {
      try {
        // Only show loading if not already in memory
        if (!memoryState.isScenarioLoaded(scenarioId)) {
          setLoading(true)
        }
        
        // Note: We rely on ScenarioLayout (or parent) to hydrate the scenario
        // This hook just reads state reactively
        const data = memoryState.getScenarioWithData(scenarioId)
        
        if (mounted) {
          setScenarioData(data || null)
          setError(null)
        }
      } catch (err: any) {
        console.error('[useList] Error loading list:', err)
        if (mounted) {
          setError(err.message || 'Failed to load list')
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    // Note: SignalR subscription is managed by ScenarioLayout (via useScenario).
    // This hook only reads state reactively.

    load()

    const unsubscribeScenario = memoryState.onStateChange(scenarioId, () => {
      if (mounted) {
        const data = memoryState.getScenarioWithData(scenarioId)
        setScenarioData(data || null)
      }
    })

    // Subscribe to UI-specific events (access control)
    // Note: snapshot-required is handled by TabSyncCoordinator.onSnapshotRequired()
    // Note: cross-tab updates are handled by TabSyncCoordinator -> memoryStateService.onStateChange()
    const unsubscribeNotifications = eventProcessor.onEvent(async (evt) => {
      if (!mounted) return

      if ('type' in evt && evt.type === 'access-denied' && evt.scenarioId === scenarioId) {
        setAccessDenied(true)
        return
      }

      if ('type' in evt && evt.type === 'access-restored' && evt.scenarioId === scenarioId) {
        setAccessDenied(false)
        return
      }
    })

    return () => {
      mounted = false
      unsubscribeScenario()
      unsubscribeNotifications()
    }
  }, [scenarioId, listId, memoryState])

  const list = useMemo(() => {
    const lists = scenarioData?.lists ?? []
    return lists.find(l => l.id === listId) ?? null
  }, [scenarioData, listId])

  const items = useMemo(() => selectSortedListItems(list), [list])

  return {
    list,
    items,
    loading,
    error,
    accessDenied,
    dismissAccessDenied: () => setAccessDenied(false)
  }
}
